import { computed, ref, watch } from 'vue'

import useInvalidateQuery from '@/composables/useInvalidateQueryData'

import { PhoneTypes } from '@/enums/phoneTypes'

import { Email } from '@/interfaces/email'
import { NotificationSettings } from '@/interfaces/notificationSettings'
import { Phone } from '@/interfaces/phone'

import useInternalUser from '@/internal/modules/users/composables/useInternalUser'

import { ToggleValue } from '@/modules/users/components/EditUserNotifications.vue'
import useUser from '@/modules/users/composables/useUser'

import useUserEmails from './useUserEmails'
import useUserPhones from './useUserPhones'

const useEditUserNotifications = (
	mainEmail: string,
	userId: number,
	settings?: NotificationSettings
) => {
	const {
		updateUserAsync: updateUserAsyncInternal,
		updateUserLoading: updateUserAsyncLoading,
	} = useInternalUser({
		immediate: false,
	})

	const { updateUserAsync, updateUserLoading } = useUser({
		immediate: false,
	})
	const { invalidateQuery } = useInvalidateQuery()

	const { usersPhones, refetch: refetchPhones } = useUserPhones({
		params: {
			userId: userId,
		},
		immediate: false,
	})

	const { userEmails, refetch: refetchEmails } = useUserEmails({
		params: {
			userId: userId,
		},
		immediate: false,
	})

	const transformedPhones = computed(() => {
		const draft = usersPhones.value.filter(
			({ type }) => type == PhoneTypes.Mobile
		)

		return draft.map((phone) => ({
			numberWithCode: `${phone.countryCode.code} ${phone.phone}`,
			number: phone.phone,
			id: phone.id,
			countryId: phone.countryCode.id,
			countryCode: phone.countryCode,
		}))
	})

	const transformedEmails = computed(() => {
		// Crear lista con emails adicionales del usuario
		const additionalEmails = userEmails.value.map(({ email, id }) => {
			return {
				email,
				id,
				isMain: false,
				canDelete: true, // Los emails adicionales se pueden eliminar
			}
		})

		// Siempre incluir el email principal al inicio
		const allEmails = [
			{
				email: mainEmail,
				id: 0,
				isMain: true,
				canDelete: false, // El email principal no se puede eliminar
			},
			...additionalEmails,
		]

		return allEmails
	})

	const form = ref<NotificationSettings>({
		services: {
			whatsapp: [] as string[],
			email: [] as string[],
		},
		incentives: {
			whatsapp: [] as string[],
			email: [] as string[],
		},
	})

	const setValue = (
		value: string[],
		category: keyof NotificationSettings,
		type: keyof NotificationSettings[keyof NotificationSettings]
	) => {
		form.value = {
			...form.value,
			[category]: {
				...form.value[category],
				[type]: value,
			},
		}
	}

	const toggleValue: ToggleValue = (value, category, type) => {
		const currentArray = form.value[category][type] || []

		const draft = [...currentArray]

		const index = draft.indexOf(value)

		if (index === -1) {
			draft.push(value)
			setValue(draft, category, type)
		} else {
			setValue(
				draft.filter((_, i) => i !== index),
				category,
				type
			)
		}

		// Guardado automático después de cambiar el valor
		handleSave()
	}

	const handleSave = async () => {
		try {
			await updateUserAsync({
				id: userId,
				notificationSettings: form.value,
			})

			// Invalidar y refrescar todas las consultas relacionadas
			await Promise.all([
				invalidateQuery(['user', userId]),
				invalidateQuery(['users']),
				refetchEmails?.(),
				refetchPhones?.(),
			])
		} catch (error) {
			console.error('Error al guardar configuración de notificaciones:', error)
			throw error
		}
	}

	watch(
		() => settings,
		(value) => {
			if (value) {
				form.value = {
					services: {
						whatsapp: value.services?.whatsapp || [],
						email: value.services?.email || [],
					},
					incentives: {
						email: value.incentives?.email || [],
						whatsapp: value.incentives?.whatsapp || [],
					},
				}
			} else {
				// Si no hay configuración previa, incluir el email principal por defecto
				form.value = {
					services: {
						whatsapp: [],
						email: [mainEmail],
					},
					incentives: {
						whatsapp: [],
						email: [mainEmail],
					},
				}
			}
		},
		{
			immediate: true,
		}
	)

	return {
		form,
		transformedPhones,
		transformedEmails,
		updateUserLoading,

		toggleValue,
		handleSave,
	}
}

export default useEditUserNotifications
